import React, { FC, useEffect, useMemo, useState } from 'react';

import { Outlet } from 'react-router';

import { PageHeader, Select, Skeleton } from '@ui';

import useTranslation from '@app/hooks/use-translation';

import { OptionItem } from '@app/components/ui/select/select.type';

import { useGetChannelsQuery } from '@app/store/api/get-channels-info.api';

import { useGetAllLiveReportsListQuery } from '@app/store/api/live-reports.api';

import {
  WordNGramStatusesType,
  WordsNGramStaticRequestType,
} from '@app/interfaces/words-n-gramm.type';

import { useGetAllDictionariesListQuery } from '@app/store/api/dictionaries.api';

import {
  useGetChatCloudStatusQuery,
  useGetChatWordsNGramSettingsQuery,
  useGetCloudStatusQuery,
  useGetWordsNGramSettingsQuery,
} from '@app/store/api/get-words-n-gramm.api';

import { NotFoundedPage } from '@app/pages';

import { useGetAllChatLiveReportsListQuery } from '@app/store/api/chat-api/live-reports.api';

import { useGetChatRolesQuery } from '@app/store/api/chat-api/get-chat-roles.api';

import { WordsCloudFilter } from './blocks/words-cloud-filter';

import { WordCloudContext } from './types/types';

import WordsCloudSkeleton from './blocks/words-cloud.skeleton';

type Template = 'chat' | 'voice';

type ProjectIds = {
  chat?: string;
  voice?: string;
};

const LOCAL_STORAGE_KEYS: Record<Template, string> = {
  chat: 'cloud_n_gram_chat',
  voice: 'cloud_n_gram',
};
type CloudDataByTemplate = {
  chat?: WordsNGramStaticRequestType;
  voice?: WordsNGramStaticRequestType;
};
const WordsNGramm: FC<{ currentTemplate: Template }> = ({ currentTemplate }) => {
  const { t } = useTranslation('pages.wordsNGramm');

  const [projectIds, setProjectIds] = useState<ProjectIds>(() => ({
    chat: localStorage.getItem(LOCAL_STORAGE_KEYS.chat) || undefined,
    voice: localStorage.getItem(LOCAL_STORAGE_KEYS.voice) || undefined,
  }));

  const projectId = projectIds[currentTemplate];
  const [cloudData, setCloudData] = useState<CloudDataByTemplate>({});
  const dataForCloud = cloudData[currentTemplate];

  const [nGramCloudStatus, changeStatus] = useState<WordNGramStatusesType>();

  const { data: dictionaries, isLoading: dictLoading } = useGetAllDictionariesListQuery();

  const { data: voiceChannels, isLoading: channelLoading } = useGetChannelsQuery(undefined, {
    skip: currentTemplate === 'chat',
  });

  const { data: chatRoles, isLoading: rolesLoading } = useGetChatRolesQuery(undefined, {
    skip: currentTemplate === 'voice',
  });

  const isTemplateDataLoading = rolesLoading || channelLoading;

  const { data: projectsListVoice, isLoading: loadingVoice } = useGetAllLiveReportsListQuery(
    {
      limit: -1,
    },
    {
      skip: currentTemplate === 'chat',
    },
  );

  const { data: projectsListChat, isLoading: loadingChats } = useGetAllChatLiveReportsListQuery(
    {
      limit: -1,
    },
    {
      skip: currentTemplate === 'voice',
    },
  );

  const projectsList = useMemo(
    () => ({
      chat: projectsListChat,
      voice: projectsListVoice,
    }),
    [projectsListChat, projectsListVoice],
  );

  const currentProjectsList = projectsList[currentTemplate];

  const projectsLoading = loadingChats || loadingVoice;

  useEffect(() => {
    if (projectId) {
      return;
    }

    const firstProjectId = currentProjectsList?.live_reports?.[0]?.live_report_id;

    if (!firstProjectId) {
      return;
    }

    localStorage.setItem(LOCAL_STORAGE_KEYS[currentTemplate], firstProjectId);

    setProjectIds((prev) => ({
      ...prev,
      [currentTemplate]: firstProjectId,
    }));
  }, [currentTemplate, currentProjectsList, projectId]);

  const { data: cloudSettingsVoice, isFetching: settingsLoadingVoice } =
    useGetWordsNGramSettingsQuery(
      {
        live_report_id: projectIds.voice || '',
      },
      {
        skip: currentTemplate !== 'voice' || !projectIds.voice,

        refetchOnMountOrArgChange: true,
      },
    );

  const { data: cloudSettingsChat, isFetching: settingsLoadingChat } =
    useGetChatWordsNGramSettingsQuery(
      {
        live_report_id: projectIds.chat || '',
      },
      {
        skip: currentTemplate !== 'chat' || !projectIds.chat,

        refetchOnMountOrArgChange: true,
      },
    );

  const cloudSettings = useMemo(
    () => ({
      chat: cloudSettingsChat,
      voice: cloudSettingsVoice,
    }),
    [cloudSettingsChat, cloudSettingsVoice],
  );

  const settingsLoading = settingsLoadingChat || settingsLoadingVoice;

  const { data: cloudStatusVoice } = useGetCloudStatusQuery(
    {
      live_report_id: projectIds.voice || '',
    },
    {
      skip: currentTemplate !== 'voice' || !projectIds.voice,

      refetchOnMountOrArgChange: true,

      pollingInterval: currentTemplate === 'voice' ? 5000 : undefined,
    },
  );

  const { data: cloudStatusChat } = useGetChatCloudStatusQuery(
    {
      live_report_id: projectIds.chat || '',
    },
    {
      skip: currentTemplate !== 'chat' || !projectIds.chat,

      refetchOnMountOrArgChange: true,

      pollingInterval: currentTemplate === 'chat' ? 5000 : undefined,
    },
  );

  const cloudStatus = useMemo(
    () => ({
      chat: cloudStatusChat,
      voice: cloudStatusVoice,
    }),
    [cloudStatusChat, cloudStatusVoice],
  );

  const currentCloudStatus = cloudStatus[currentTemplate];

  useEffect(() => {
    changeStatus(currentCloudStatus?.status);
  }, [currentCloudStatus]);

  useEffect(() => {
    if (!projectId) {
      setCloudData((prev) => ({
        ...prev,
        [currentTemplate]: undefined,
      }));

      return;
    }

    if (currentCloudStatus?.status !== 'READY') {
      return;
    }

    setCloudData((prev) => ({
      ...prev,
      [currentTemplate]: {
        limit: 100,
        live_report_id: projectId,
      },
    }));
  }, [currentCloudStatus?.status, currentTemplate, projectId]);

  function handleSelectOnChange(option: string | number) {
    const nextProjectId = String(option);

    localStorage.setItem(LOCAL_STORAGE_KEYS[currentTemplate], nextProjectId);

    setProjectIds((prev) => ({
      ...prev,
      [currentTemplate]: nextProjectId,
    }));

    changeStatus(undefined);

    setCloudData((prev) => ({
      ...prev,
      [currentTemplate]: undefined,
    }));
  }

  function refetchStatus() {
    changeStatus(undefined);
  }

  function onFormSubmit(data: Omit<WordsNGramStaticRequestType, 'live_report_id'>) {
    if (!projectId) {
      return;
    }

    setCloudData((prev) => ({
      ...prev,
      [currentTemplate]: {
        ...data,
        live_report_id: projectId,
      },
    }));
  }

  const renderSelectProjectOptions: Array<OptionItem> =
    currentProjectsList?.live_reports?.map(({ name, live_report_id }) => ({
      title: name,
      value: live_report_id,
    })) || [];

  const renderSelectProject = projectsLoading ? (
    <Skeleton width={234} height={32} />
  ) : (
    <Select
      size={240}
      onChange={handleSelectOnChange}
      placeholder={t('select_all_projects')}
      options={renderSelectProjectOptions}
      defaultValue={projectId}
      isEmpty
      withSearch
      dropWidth={238}
    />
  );

  if (projectsLoading || isTemplateDataLoading || dictLoading || settingsLoading) {
    return <WordsCloudSkeleton />;
  }

  if (!currentProjectsList?.live_reports?.length) {
    return <NotFoundedPage text={t('error_page')} />;
  }

  return (
    <>
      <PageHeader label={t('module_name')} icon="CloudIcon">
        <div className="inline-flex font-[500] text-[15px] text-1color items-center">
          {t('choose_project')}:<div className="ml-[11px]">{renderSelectProject}</div>
        </div>
      </PageHeader>

      {nGramCloudStatus === 'CALCULATING' || nGramCloudStatus === 'NEED_CALCULATE' ? (
        <WordsCloudSkeleton withoutPageHeader label={t('system.cloud_is_calculating')} />
      ) : (
        <div className="relative h-[calc(100%-90px)] w-full">
          <WordsCloudFilter
            projectId={projectId}
            currentTemplate={currentTemplate}
            disableSaveButton={!!projectId}
            onFormSubmit={onFormSubmit}
            channels={voiceChannels}
            chatRoles={chatRoles}
            dictionaries={dictionaries || []}
            cloudSettings={cloudSettings[currentTemplate]}
            refetchCloudStatus={refetchStatus}
            cloudStatus={nGramCloudStatus}
            analyticsProjectByReport={
              currentProjectsList?.live_reports.find(
                (report) => report.live_report_id === projectId,
              )?.project
            }
          />

          <div className="absolute left-0 bottom-0 w-[calc(100%-410px)] h-[calc(100%-90px)]">
            <Outlet
              context={
                {
                  data: dataForCloud,
                  cloudWasCalculated: cloudSettings[currentTemplate]?.dt_calc,
                  currentTemplate,
                } as WordCloudContext
              }
            />
          </div>
        </div>
      )}
    </>
  );
};

export default WordsNGramm;
